import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Chip, TextField, MenuItem, Alert,
  Select, Checkbox, ListItemText, OutlinedInput,
  useMediaQuery, useTheme, Tab, Tabs, Divider,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, FitnessCenter as WellnessIcon } from '@mui/icons-material';
import { useForm, useWatch } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import QuillToolbarWithFontSize from '../components/editor/QuillToolbarWithFontSize';
import { resetColorHandler, colorTooltipSx, applyColorTooltips } from '../components/editor/quillColorReset';
import { useAuth } from '../context/AuthContext';
import { wellnessItemService, WellnessItem, WellnessItemType } from '../services/wellnessItemService';
import diagnosisTypeService, { DiagnosisType } from '../services/diagnosisTypeService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { wellnessItemColumns } from '../config/wellnessItemColumns';
import { formatDate } from '../utils/dateUtils';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import WellnessItemEntriesTab from '../components/wellness/WellnessItemEntriesTab';
import WellnessItemEntryDialog from '../components/wellness/WellnessItemEntryDialog';
import WellnessItemNestedEditDialog from '../components/wellness/WellnessItemNestedEditDialog';
import WellnessItemMainItemEditDialog from '../components/wellness/WellnessItemMainItemEditDialog';
import { WellnessItemEntry } from '../types/wellnessItemEntry';

// Register style-based font sizes (no-op if already registered by another module)
const SizeStylePage = Quill.import('attributors/style/size') as any;
SizeStylePage.whitelist = Array.from({ length: 93 }, (_, i) => `${i + 8}px`);
Quill.register(SizeStylePage, true);

// Block-scoped line-height attributor, applied alongside font-size to keep
// line spacing proportional to size. Whitelist must cover every value `lineHeightFor` can emit.
const ParchmentPage = Quill.import('parchment') as any;
const LineHeightStylePage = new ParchmentPage.Attributor.Style('lineheight', 'line-height', {
  scope: ParchmentPage.Scope.BLOCK,
  whitelist: ['1.5', '1.55', '1.6', '1.65'],
});
Quill.register(LineHeightStylePage, true);

const lineHeightFor = (size: number): string => {
  if (size <= 14) return '1.5';
  if (size <= 18) return '1.55';
  if (size <= 22) return '1.6';
  return '1.65';
};

type FormData = {
  name: string;
  type: WellnessItemType;
  item_content_type?: string;
  item_url?: string;
  item_title?: string;
  item_subtitle?: string;
  item_duration?: string;
};

const QUILL_MODULES = {
  toolbar: {
    container: '#quill-toolbar-wellness-page',
    handlers: {
      color(this: any, value: string) {
        const range = this.quill.getSelection();
        const colorValue = value || 'rgb(0, 0, 0)';
        if (range && range.length > 0) {
          this.quill.formatText(range.index, range.length, 'color', colorValue, 'user');
        } else {
          this.quill.format('color', colorValue, 'user');
        }
      },
      resetColor: resetColorHandler,
    },
  },
};
// 'header' kept so legacy H1/H2/H3 content in existing items survives load/save,
// even though the header control has been removed from the toolbar.
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'color', 'background', 'size', 'lineheight'];
const isQuillEmpty = (val: string) => !val || val === '<p><br></p>';


const CONTENT_TYPES = [
  { value: '', label: 'None' },
  { value: 'picture', label: 'Picture' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'content', label: 'Content' },
];

const ITEM_TYPES = [
  { value: 'item', label: 'Item' },
  { value: 'category', label: 'Category' },
];

const WellnessItemPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WellnessItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<WellnessItem | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'content'>('overview');


  const [mainItemEntry, setMainItemEntry] = useState<WellnessItemEntry | null>(null);
  const [itemEntries, setItemEntries] = useState<WellnessItemEntry[]>([]);
  const [itemsTitle, setItemsTitle] = useState('');
  const [itemText, setItemText] = useState('');


  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entryDialogTitle, setEntryDialogTitle] = useState('');
  const [editingEntry, setEditingEntry] = useState<WellnessItemEntry | null>(null);
  const [entryContext, setEntryContext] = useState<'main_item' | 'items'>('items');
  const [deleteEntryDialogOpen, setDeleteEntryDialogOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<WellnessItemEntry | null>(null);

  const quillRef = useRef<ReactQuill>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);
  const [itemFontSize, setItemFontSize] = useState(14);

  const handleItemFontSizeChange = (size: number) => {
    setItemFontSize(size);
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    const lh = lineHeightFor(size);
    if (!range || range.length === 0) {
      editor.format('size', `${size}px`, 'user');
      editor.format('lineheight', lh, 'user');
    } else {
      editor.formatText(range.index, range.length, 'size', `${size}px`, 'user');
      editor.formatLine(range.index, range.length, 'lineheight', lh, 'user');
    }
  };

  const [diagnosisTypes, setDiagnosisTypes] = useState<DiagnosisType[]>([]);
  const [selectedDiagnosisTypeIds, setSelectedDiagnosisTypeIds] = useState<number[]>([]);

  const [allWellnessItems, setAllWellnessItems] = useState<WellnessItem[]>([]);
  const [nestedEditItem, setNestedEditItem] = useState<WellnessItem | null>(null);
  const [nestedEditOpen, setNestedEditOpen] = useState(false);
  const [mainItemInlineEditOpen, setMainItemInlineEditOpen] = useState(false);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormData>();
  const [pictureFileId, setPictureFileId] = useState<number | null>(null);
  const [picturePreviewUrl, setPicturePreviewUrl] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const watchedType = useWatch({ control, name: 'type' });
  const watchedContentType = useWatch({ control, name: 'item_content_type' });
  const isPicture = watchedContentType === 'picture';

  // Decorate the colour toolbar controls (Clear-color icon + instant tooltips)
  // once the editor is in the DOM. Re-runs when the dialog opens / type changes.
  useEffect(() => {
    if (!addDialogOpen) return;
    const id = requestAnimationFrame(() => applyColorTooltips(editorWrapRef.current));
    return () => cancelAnimationFrame(id);
  }, [addDialogOpen, watchedType]);
  const getSortValue = useCallback((item: WellnessItem, key: string): any => {
    switch (key) {
      case 'name': return item.name.toLowerCase();
      case 'type': return item.type;
      case 'createdAt': return item.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((item: WellnessItem): string[] => [item.name, item.type], []);

  const table = useTablePage<WellnessItem>({
    entityType: 'wellness-items',
    columns: wellnessItemColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<WellnessItem>({
    table,
    createFn: (data) => wellnessItemService.create(currentUser!.tenant_id!, data),
    updateFn: (id, data) => wellnessItemService.update(id as number, data),
    deleteFn: (id) => wellnessItemService.delete(id as number),
    entityName: 'Wellness Item',
    onSuccess: () => { setAddDialogOpen(false); setEditingItem(null); reset(); setActiveTab('overview'); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setItemToDelete(null); },
  });

  useEffect(() => {
    if (currentUser?.user_privilege?.wellness) {
      table.loadData(() => wellnessItemService.getAll(currentUser.tenant_id ?? undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (addDialogOpen && currentUser?.tenant_id) {
      wellnessItemService.getAll(currentUser.tenant_id).then(setAllWellnessItems).catch(() => {});
    }
  }, [addDialogOpen, currentUser]);

  useEffect(() => {
    if (currentUser?.user_privilege?.wellness && currentUser.tenant_id != null) {
      diagnosisTypeService.getAll(currentUser.tenant_id).then(setDiagnosisTypes).catch(() => {});
    }
  }, [currentUser]);

  const attachKey = (entry: any): WellnessItemEntry => ({
    ...entry,
    wellness_item_id: entry.wellness_item_id ?? null,
    _key: entry._key ?? Math.random().toString(36).slice(2),
  });

  const openAdd = () => {
    setEditingItem(null);
    setMainItemEntry(null);
    setItemEntries([]);
    setItemsTitle('');
    setActiveTab('overview');
    setItemText('');
    setItemFontSize(14);
    setPictureFileId(null);
    setPicturePreviewUrl(null);
    setSelectedDiagnosisTypeIds([]);
    reset({ name: '', type: 'item', item_content_type: '', item_url: '', item_title: '', item_subtitle: '', item_duration: '' });
    setAddDialogOpen(true);
  };

  const openEdit = async (item: WellnessItem) => {
    setEditingItem(item);
    setActiveTab('overview');
    setSelectedDiagnosisTypeIds(item.diagnosis_type_ids ?? []);
    reset({
      name: item.name,
      type: item.type,
      item_content_type: item.data?.content_type ?? '',
      item_url: item.data?.url ?? '',
      item_title: item.data?.title ?? '',
      item_subtitle: item.data?.subtitle ?? '',
      item_duration: item.data?.duration ?? '',
    });
    setItemText(item.data?.text ?? '');
    setItemFontSize(14);
    const fid = item.data?.picture_file_id ?? null;
    setPictureFileId(fid);
    setPicturePreviewUrl(null);
    if (fid) {
      wellnessItemService.getPictureBlobUrl(fid).then(setPicturePreviewUrl).catch(() => {});
    }
    setMainItemEntry(item.data?.main_item ? attachKey(item.data.main_item) : null);
    setItemEntries((item.data?.items ?? []).map(attachKey));
    setItemsTitle(item.data?.items_title ?? '');
    setAddDialogOpen(true);
  };


  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPicture(true);
    try {
      const { file_id } = await wellnessItemService.uploadPicture(file);
      setPictureFileId(file_id);
      setPicturePreviewUrl(URL.createObjectURL(file));
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleEdit = () => { if (table.selectedItem) openEdit(table.selectedItem); table.handleMenuClose(); };
  const handleDelete = () => {
    if (table.selectedItem) { setItemToDelete(table.selectedItem); setDeleteDialogOpen(true); }
    table.handleMenuClose();
  };

  const onSubmit = (formData: FormData) => {
    const stripKey = ({ _key, ...e }: WellnessItemEntry) => e; // eslint-disable-line @typescript-eslint/no-unused-vars
    let data: any;
    if (formData.type === 'category') {
      data = {
        title: formData.item_title?.trim() || null,
        subtitle: formData.item_subtitle?.trim() || null,
        main_item: mainItemEntry ? stripKey(mainItemEntry) : null,
        items_title: itemsTitle.trim() || null,
        items: itemEntries.map(stripKey),
      };
    } else {
      const ct = formData.item_content_type || '';
      data = {
        content_type: ct || null,
        title: formData.item_title?.trim() || null,
        subtitle: formData.item_subtitle?.trim() || null,
        picture_file_id: ct === 'picture' ? pictureFileId : null,
        url: ct !== 'picture' ? (formData.item_url?.trim() || null) : null,
        text: isQuillEmpty(itemText) ? null : itemText,
      };
    }
    crud.handleCreateOrUpdate(
      { name: formData.name, type: formData.type, data, diagnosis_type_ids: selectedDiagnosisTypeIds },
      editingItem,
    );
  };

  const openAddEntry = (context: 'main_item' | 'items') => {
    setEntryContext(context);
    setEditingEntry(null);
    setEntryDialogTitle(context === 'main_item' ? 'Set Main Item' : 'Add Item');
    setEntryDialogOpen(true);
  };

const handleSaveEntry = (entry: WellnessItemEntry) => {
    if (entryContext === 'main_item') {
      setMainItemEntry(entry);
    } else {
      setItemEntries((prev) =>
        editingEntry
          ? prev.map((e) => e._key === editingEntry._key ? entry : e)
          : [...prev, entry]
      );
    }
    setEntryDialogOpen(false);
  };

  const handleConfirmDeleteEntry = () => {
    if (deletingEntry) {
      if (entryContext === 'main_item') {
        setMainItemEntry(null);
      } else {
        setItemEntries((prev) => prev.filter((e) => e._key !== deletingEntry._key));
      }
    }
    setDeleteEntryDialogOpen(false);
    setDeletingEntry(null);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => [
    { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
    { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' },
  ];

  const TYPE_COLORS: Record<string, 'primary' | 'secondary'> = { item: 'primary', category: 'secondary' };

  const getColumnValue = (item: WellnessItem, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Typography variant="body2" onClick={(e) => { e.stopPropagation(); openEdit(item); }}
            sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', '&:hover': { color: 'primary.dark' } }}>
            {item.name}
          </Typography>
        );
      case 'type':
        return <Chip label={item.type === 'item' ? 'Item' : 'Category'} color={TYPE_COLORS[item.type] || 'default'} size="small" />;
      case 'createdAt': return formatDate(item.created_at);
      default: return 'N/A';
    }
  };

  const renderMobileCard = (item: WellnessItem) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{item.name}</Typography>
        <Chip label={item.type === 'item' ? 'Item' : 'Category'} color={TYPE_COLORS[item.type] || 'default'} size="small" />
      </Box>
    </>
  );

  const isCategory = watchedType === 'category';

  if (!currentUser?.user_privilege?.wellness) {
    return <Alert severity="error">You do not have permission to access this page.</Alert>;
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading wellness items:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Wellness Items"
        searchPlaceholder="Search wellness items..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
            Add Item
          </Button>
        }
      />
      {isMobile ? (
        <MobileCardGrid data={table.sortedData} renderCard={renderMobileCard} onMenuClick={table.handleRowClick}
          getItemKey={(i) => i.id} emptyIcon={<WellnessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No wellness items found" />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody data={table.sortedData} {...table.tableBodyProps} getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick} getRowKey={(i) => i.id} />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      <ClinicDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title={editingItem ? 'Edit Wellness Item' : 'Add Wellness Item'}
        closeButtonSide="left"
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingItem ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
          </>
        }
      >
        <Box sx={{ minWidth: 480 }}>
          {isCategory && (
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Overview" value="overview" />
              <Tab label="Content" value="content" />
            </Tabs>
          )}

          {(!isCategory || activeTab === 'overview') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
                <TextField {...register('name', { required: 'Name is required' })} fullWidth size="small"
                  error={!!errors.name} helperText={errors.name?.message as string}
                  inputProps={{ dir: 'rtl' }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Type</Typography>
                <TextField {...register('type', { required: 'Type is required' })} select fullWidth size="small"
                  defaultValue={editingItem?.type || 'item'}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  >
                  {ITEM_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Box>
              {watchedType !== 'category' && (
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Content Type</Typography>
                  <TextField {...register('item_content_type')} select fullWidth size="small"
                    defaultValue={editingItem?.data?.content_type ?? ''}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    >
                    {CONTENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                </Box>
              )}
              {watchedType !== 'category' && isPicture && (
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Picture</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    {picturePreviewUrl && (
                      <Box component="img" src={picturePreviewUrl} alt="preview"
                        sx={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                    )}
                    <Button component="label" variant="outlined" size="small" disabled={uploadingPicture}>
                      {uploadingPicture ? 'Uploading...' : picturePreviewUrl ? 'Change Picture' : 'Upload Picture'}
                      <input type="file" hidden accept="image/*" onChange={handlePictureChange} />
                    </Button>
                  </Box>
                </Box>
              )}
              {watchedType !== 'category' && !isPicture && (
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>URL</Typography>
                  <TextField {...register('item_url')} fullWidth size="small"
                    placeholder="https://..."
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
                </Box>
              )}
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Title</Typography>
                <TextField {...register('item_title')} fullWidth size="small"
                  inputProps={{ dir: 'rtl' }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Subtitle</Typography>
                <TextField {...register('item_subtitle')} fullWidth size="small"
                  inputProps={{ dir: 'rtl' }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Diagnosis Types</Typography>
                <Select
                  multiple
                  fullWidth
                  size="small"
                  value={selectedDiagnosisTypeIds}
                  onChange={(e) => setSelectedDiagnosisTypeIds(e.target.value as number[])}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const dt = diagnosisTypes.find((t) => t.id === id);
                        return dt ? <Chip key={id} label={dt.name} size="small" /> : null;
                      })}
                    </Box>
                  )}
                  sx={CLINIC_DIALOG_SX.select}
                >
                  {diagnosisTypes.map((dt) => (
                    <MenuItem key={dt.id} value={dt.id}>
                      <Checkbox checked={selectedDiagnosisTypeIds.includes(dt.id)} />
                      <ListItemText primary={dt.name} />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {/* Text — shown for item type, hidden when content type is 'content' */}
              {watchedType !== 'category' && (
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Text</Typography>
                  <Box ref={editorWrapRef} className="ql-rtl-wrapper" sx={{ flex: 1, '& .ql-editor': { direction: 'rtl', textAlign: 'right', fontSize: '14px' }, '& .ql-container': { minHeight: 120 }, ...colorTooltipSx }}>
                    <QuillToolbarWithFontSize
                      toolbarId="quill-toolbar-wellness-page"
                      fontSize={itemFontSize}
                      onFontSizeChange={handleItemFontSizeChange}
                    />
                    <ReactQuill
                      ref={quillRef}
                      value={itemText}
                      onChange={setItemText}
                      onChangeSelection={(range) => {
                        if (!range) return;
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const format = editor.getFormat(range.index, range.length);
                        const raw = Array.isArray(format.size) ? format.size[0] : format.size;
                        const parsed = parseInt(String(raw ?? ''), 10);
                        setItemFontSize(!isNaN(parsed) ? parsed : 14);
                      }}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {isCategory && activeTab === 'content' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Main Item</Typography>
                  <Button variant="outlined" size="small" onClick={() => setMainItemInlineEditOpen(true)}>
                    Edit
                  </Button>
                </Box>
                {mainItemEntry ? (() => {
                  const ref = allWellnessItems.find((w) => w.id === mainItemEntry.wellness_item_id);
                  return (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary">Linked: {ref?.name ?? `Item #${mainItemEntry.wellness_item_id}`}</Typography>
                      {mainItemEntry.title && <Typography variant="body2" display="block"><strong>{mainItemEntry.title}</strong></Typography>}
                      {mainItemEntry.subtitle && <Typography variant="caption" color="text.secondary" display="block">{mainItemEntry.subtitle}</Typography>}
                    </Box>
                  );
                })() : (
                  <Typography variant="body2" color="text.secondary">No main item set</Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Items</Typography>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Items Title</Typography>
                  <TextField
                    value={itemsTitle}
                    onChange={(e) => setItemsTitle(e.target.value)}
                    fullWidth size="small"
                    placeholder="e.g. Recommended exercises"
                    inputProps={{ dir: 'rtl' }}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    sx={{ mb: 1.5 }}
                  />
                </Box>
                <WellnessItemEntriesTab
                  entries={itemEntries}
                  onReorder={setItemEntries}
                  allWellnessItems={allWellnessItems}
                  onAdd={() => openAddEntry('items')}
                  onEdit={(entry) => { setEditingEntry(entry); setEntryDialogTitle('Edit Item'); setEntryDialogOpen(true); }}
                  onDelete={(entry) => { setEntryContext('items'); setDeletingEntry(entry); setDeleteEntryDialogOpen(true); }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </ClinicDialog>

      <WellnessItemEntryDialog
        open={entryDialogOpen}
        onClose={() => setEntryDialogOpen(false)}
        title={entryDialogTitle}
        editingEntry={editingEntry}
        allWellnessItems={allWellnessItems}
        onSave={handleSaveEntry}
      />

      <WellnessItemMainItemEditDialog
        open={mainItemInlineEditOpen}
        onClose={() => setMainItemInlineEditOpen(false)}
        entry={mainItemEntry}
        allWellnessItems={allWellnessItems}
        onSave={(updatedEntry) => {
          setMainItemEntry(updatedEntry);
          setMainItemInlineEditOpen(false);
        }}
      />


      <WellnessItemNestedEditDialog
        open={nestedEditOpen}
        onClose={() => setNestedEditOpen(false)}
        item={nestedEditItem}
        onSaved={(updated) => {
          setNestedEditOpen(false);
          setNestedEditItem(updated);
          setAllWellnessItems((prev) => prev.map((w) => w.id === updated.id ? updated : w));
          table.setData(table.data.map((w) => w.id === updated.id ? updated : w));
          if (currentUser?.tenant_id) {
            wellnessItemService.getAll(currentUser.tenant_id).then((items) => {
              setAllWellnessItems(items);
              table.setData(items);
            }).catch(() => {});
          }
        }}
      />

      <ConfirmDeleteDialog
        open={deleteEntryDialogOpen}
        onClose={() => { setDeleteEntryDialogOpen(false); setDeletingEntry(null); }}
        onConfirm={handleConfirmDeleteEntry}
        entityType="Item"
        entityName={allWellnessItems.find((w) => w.id === deletingEntry?.wellness_item_id)?.name}
      />

      <ConfirmDeleteDialog open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={() => crud.handleDelete(itemToDelete)}
        entityType="Wellness Item" entityName={itemToDelete?.name} />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default WellnessItemPage;

